import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, LogIn, UserPlus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [activeTab, setActiveTab] = useState('login');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    nickname: '',
    club: '',
    phone: '',
    privacy_accepted: false
  });

  useEffect(() => {
    loadClubs();
  }, []);

  const loadClubs = async () => {
    try {
      const res = await axios.get(`${API}/clubs`);
      setClubs(res.data);
    } catch (error) {
      console.error('Failed to load clubs');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData, { withCredentials: true });
      navigate('/dashboard', { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.privacy_accepted) {
      toast.error(t('privacyRequired'));
      return;
    }
    
    if (!registerData.club) {
      toast.error(t('selectYourClub'));
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/register`, registerData, { withCredentials: true });
      toast.success(t('registrationSuccess'));
      // Clear the form
      setRegisterData({
        email: '',
        password: '',
        name: '',
        nickname: '',
        club: '',
        phone: '',
        privacy_accepted: false
      });
      // Switch to login tab so user can login with their new credentials
      setActiveTab('login');
      // Pre-fill the login email
      setLoginData(prev => ({ ...prev, email: registerData.email }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-3 sm:p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title with Animation */}
        <div className="text-center mb-6 sm:mb-8 animate-fade-in">
          <div className="inline-block p-2 sm:p-3 bg-white/10 backdrop-blur-xl rounded-2xl mb-3 sm:mb-4 hover:scale-105 transition-transform duration-300">
            <img 
              src="https://customer-assets.emergentagent.com/job_7442c566-20f0-4c0f-8d05-f273c41d0090/artifacts/usp3jo2t_DSV_Niners_Logo.png"
              alt="Logo"
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
            />
          </div>
          <h1 
            className="text-2xl sm:text-4xl font-semibold text-white mb-1 sm:mb-2 tracking-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t('ranking')}
          </h1>
          <p className="text-sm sm:text-base text-gray-400">{t('trackEloCompete')}</p>
        </div>

        <Card className="bg-[#111111] border-[#27272A] shadow-2xl animate-slide-up">
          <CardHeader className="px-4 sm:px-6 pb-2">
            <CardTitle className="text-white text-lg sm:text-xl">{t('welcome')}</CardTitle>
            <CardDescription className="text-gray-400 text-sm">{t('signInOrCreate')}</CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6 bg-[#0A0A0A] p-1">
                <TabsTrigger 
                  value="login" 
                  data-testid="login-tab" 
                  className="text-sm sm:text-base data-[state=active]:bg-[#007AFF] data-[state=active]:text-white transition-all duration-200"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  {t('login')}
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  data-testid="register-tab" 
                  className="text-sm sm:text-base data-[state=active]:bg-[#007AFF] data-[state=active]:text-white transition-all duration-200"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t('register')}
                </TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login" className="animate-fade-in">
                <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-white text-sm">{t('email')}</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="you@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      required
                      className="bg-[#0A0A0A] border-[#27272A] text-white text-sm sm:text-base h-10 sm:h-11 focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-white text-sm">{t('password')}</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      required
                      className="bg-[#0A0A0A] border-[#27272A] text-white text-sm sm:text-base h-10 sm:h-11 focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
                    />
                    <div className="text-right">
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => navigate('/forgot-password')}
                        className="text-[#007AFF] hover:text-[#0066D6] p-0 h-auto text-sm"
                      >
                        {t('forgotPassword')}
                      </Button>
                    </div>
                  </div>
                  <Button 
                    type="submit"
                    data-testid="login-submit-btn"
                    disabled={isLoading}
                    className="w-full bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-xl h-10 sm:h-11 font-semibold transition-all duration-200 hover:shadow-lg hover:shadow-[#007AFF]/25"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('signingIn')}</>
                    ) : (
                      <><LogIn className="w-4 h-4 mr-2" /> {t('signIn')}</>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register" className="animate-fade-in">
                <form onSubmit={handleRegister} className="space-y-3 sm:space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="reg-name" className="text-white text-sm">{t('name')} *</Label>
                      <Input
                        id="reg-name"
                        data-testid="register-name-input"
                        type="text"
                        placeholder="Max Mustermann"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                        required
                        className="bg-[#0A0A0A] border-[#27272A] text-white text-sm h-10 focus:ring-2 focus:ring-[#007AFF] transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-nickname" className="text-white text-sm">{t('nickname')} <span className="text-gray-500">{t('optional')}</span></Label>
                      <Input
                        id="reg-nickname"
                        data-testid="register-nickname-input"
                        type="text"
                        placeholder="MM"
                        value={registerData.nickname}
                        onChange={(e) => setRegisterData({...registerData, nickname: e.target.value})}
                        className="bg-[#0A0A0A] border-[#27272A] text-white text-sm h-10 focus:ring-2 focus:ring-[#007AFF] transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email" className="text-white text-sm">{t('email')} *</Label>
                    <Input
                      id="reg-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="you@example.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                      required
                      className="bg-[#0A0A0A] border-[#27272A] text-white text-sm h-10 focus:ring-2 focus:ring-[#007AFF] transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password" className="text-white text-sm">{t('password')} *</Label>
                    <Input
                      id="reg-password"
                      data-testid="register-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      required
                      className="bg-[#0A0A0A] border-[#27272A] text-white text-sm h-10 focus:ring-2 focus:ring-[#007AFF] transition-all"
                    />
                  </div>
                  {clubs.length > 0 ? (
                    <div className="space-y-2">
                      <Label className="text-white text-sm flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> {t('club')} <span className="text-red-400">*</span>
                      </Label>
                      <Select
                        value={registerData.club}
                        onValueChange={(value) => setRegisterData({...registerData, club: value})}
                        required
                      >
                        <SelectTrigger 
                          data-testid="register-club-select"
                          className="bg-[#0A0A0A] border-[#27272A] text-white h-10"
                        >
                          <SelectValue placeholder={t('selectYourClub')} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111111] border-[#27272A]">
                          <SelectItem value="no_club" className="text-gray-400">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">—</span>
                              {t('noClubOption')}
                            </div>
                          </SelectItem>
                          {clubs.map(club => (
                            <SelectItem key={club.club_id} value={club.name} className="text-white">
                              <div className="flex items-center gap-2">
                                {club.logo_url && (
                                  <img src={club.logo_url} alt="" className="w-5 h-5 object-contain" />
                                )}
                                {club.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">{t('noClubHint')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-white text-sm flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> {t('club')} <span className="text-red-400">*</span>
                      </Label>
                      <Select
                        value={registerData.club}
                        onValueChange={(value) => setRegisterData({...registerData, club: value})}
                        required
                      >
                        <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white h-10">
                          <SelectValue placeholder={t('selectYourClub')} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111111] border-[#27272A]">
                          <SelectItem value="no_club" className="text-gray-400">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">—</span>
                              {t('noClubOption')}
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">{t('noClubHint')}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="reg-phone" className="text-white text-sm">{t('phone')} <span className="text-gray-500">{t('optional')}</span></Label>
                    <Input
                      id="reg-phone"
                      type="tel"
                      placeholder="+43 123 456 789"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                      className="bg-[#0A0A0A] border-[#27272A] text-white text-sm h-10 focus:ring-2 focus:ring-[#007AFF] transition-all"
                    />
                  </div>
                  <div className="flex items-start space-x-3 p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                    <Checkbox
                      id="privacy"
                      data-testid="privacy-checkbox"
                      checked={registerData.privacy_accepted}
                      onCheckedChange={(checked) => setRegisterData({...registerData, privacy_accepted: checked})}
                      className="mt-0.5"
                    />
                    <Label htmlFor="privacy" className="text-sm text-gray-400 cursor-pointer leading-relaxed">
                      {t('privacyPolicyText')}
                    </Label>
                  </div>
                  <Button 
                    type="submit"
                    data-testid="register-submit-btn"
                    disabled={isLoading}
                    className="w-full bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-xl h-10 sm:h-11 font-semibold transition-all duration-200 hover:shadow-lg hover:shadow-[#007AFF]/25"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('creatingAccount')}</>
                    ) : (
                      <><UserPlus className="w-4 h-4 mr-2" /> {t('createAccount')}</>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Back to home link */}
        <div className="text-center mt-4">
          <Button
            variant="link"
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← {t('backToHome')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
